import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  FileText, Upload, LogOut, User, DollarSign, BarChart, 
  AlertTriangle, Shield, TrendingUp, Calendar, ChevronRight,
  Bell, Edit, X, Building2
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { AlertsSummary, AlertsPanel } from '../components/AlertsPanel';
import ContractEditModal from '../components/ContractEditModal';
import OnboardingWizard from '../components/OnboardingWizard';

// Risk Level Badge Component - Uses ONLY backend risk_score/risk_level (Single Source of Truth)
const getRiskFromScore = (contract) => {
  const level = (contract.risk_level || '').toLowerCase();
  if (level === 'alto' || level === 'high') return 'high';
  if (level === 'medio' || level === 'medium') return 'medium';
  if (level === 'bajo' || level === 'low') return 'low';
  const score = parseFloat(contract.risk_score);
  if (!isNaN(score)) {
    if (score >= 7) return 'high';
    if (score >= 4) return 'medium';
    return 'low';
  }
  return 'low';
};

const RiskBadge = ({ level, score }) => {
  const config = {
    low: { bg: '#D1FAE5', text: '#065F46', border: '#34D399', label: 'Bajo' },
    medium: { bg: '#FEF3C7', text: '#92400E', border: '#FBBF24', label: 'Medio' },
    high: { bg: '#FEE2E2', text: '#991B1B', border: '#F87171', label: 'Alto' }
  };
  const style = config[level] || config.medium;
  
  return (
    <div className="flex items-center gap-2">
      <span 
        className="text-xs font-medium px-2 py-1 rounded-full border"
        style={{ backgroundColor: style.bg, color: style.text, borderColor: style.border }}
      >
        {style.label}
      </span>
      
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subValue, color = '#1A4D2E', onClick }) => (
  <div 
    className={`bg-white border border-border shadow-sm rounded-lg p-5 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
    onClick={onClick}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
        <p className="text-2xl font-bold" style={{ color }}>{value}</p>
        {subValue && <p className="text-xs text-gray-500 mt-1">{subValue}</p>}
      </div>
      <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}15` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [comparisons, setComparisons] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAlerts, setShowAlerts] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [alertsCount, setAlertsCount] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
    fetchAlertsCount();
    // Show onboarding wizard for first-time users (per-user key)
    if (!localStorage.getItem(`cc360_onboarding_done_${user.id}`)) {
      setShowOnboarding(true);
    }
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [contractsData, statsData, comparisonsData] = await Promise.all([
        api.getContracts(),
        api.getDashboardStats(),
        api.getComparisons()
      ]);
      setContracts(contractsData);
      setStats(statsData);
      setComparisons(comparisonsData);
    } catch (error) {
      // Fallback if stats endpoint not ready
      try {
        const contractsData = await api.getContracts();
        setContracts(contractsData);
        const comparisonsData = await api.getComparisons();
        setComparisons(comparisonsData);
      } catch (e) {
        toast.error('Error al cargar datos');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAlertsCount = async () => {
    try {
      const data = await api.getAlertsCount();
      setAlertsCount(data.total || 0);
    } catch (e) {
      // Silent fail
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  if (!user) return null;

  const analyzedContracts = contracts.filter(c => c.status === 'analyzed');
  const highRiskContracts = contracts.filter(c => getRiskFromScore(c) === 'high');

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F0' }}>
      {/* Navigation */}
      <nav className="bg-white border-b border-border shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              {/* Ajuste: Logo más grande y visible */}
              <img src="/assets/logo.png" alt="contractcheck360" className="h-10 md:h-12 w-auto object-contain" />
              {/* Nombre oculto en móvil, visible en escritorio */}
              <span className="text-xl font-bold hidden md:block" style={{ color: '#1A4D2E' }}>
                ContractCheck360
              </span>
            </div>
            <div className="flex items-center gap-3">
              {/* Alerts Bell */}
              <button 
                onClick={() => setShowAlerts(!showAlerts)}
                className="relative p-2 hover:bg-gray-100 rounded-full transition-colors"
                data-testid="alerts-bell-btn"
              >
                <Bell className="w-5 h-5 text-gray-600" />
                {alertsCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {alertsCount > 9 ? '9+' : alertsCount}
                  </span>
                )}
              </button>
              
              {/* Profile Link */}
              <Link to="/profile" className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-testid="profile-link">
                <User className="w-5 h-5 text-gray-600" />
              </Link>
              
              <span className="text-sm hidden md:block" style={{ color: '#64748B' }}>{user.full_name}</span>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="border-primary text-primary hover:bg-primary hover:text-white"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {/* Ajuste: Siempre dice Salir tanto en móvil como escritorio */}
                <span>Salir</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-6">
        {/* Welcome */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#1A4D2E' }}>
            Hola, {user.full_name.split(' ')[0]}
          </h1>
          <p className="text-sm text-gray-500">Aquí está el resumen de tus contratos</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard 
            icon={FileText} 
            label="Total Contratos" 
            value={stats?.total_contracts || contracts.length}
            subValue={`${stats?.analyzed_contracts || analyzedContracts.length} analizados`}
          />
          <StatCard 
            icon={TrendingUp} 
            label="Riesgo Promedio" 
            value={stats?.average_risk_score ? `${Math.round(stats.average_risk_score)}` : '-'}
            subValue="de 10 puntos"
            color="#D97706"
          />
          <StatCard 
            icon={AlertTriangle} 
            label="Alto Riesgo" 
            value={stats?.high_risk_contracts || highRiskContracts.length}
            subValue="contratos"
            color="#DC2626"
          />
          <StatCard 
            icon={Shield} 
            label="Bajo Riesgo" 
            value={stats?.risk_distribution?.low || 0}
            subValue="contratos seguros"
            color="#059669"
          />
        </div>

        {/* Alerts Summary */}
        <AlertsSummary />

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Link 
            to="/upload" 
            className="bg-white border-2 border-dashed hover:border-solid transition-all rounded-lg p-5 flex items-center gap-4 hover:shadow-md"
            style={{ borderColor: '#1A4D2E' }}
            data-testid="upload-card"
          >
            <div className="p-3 rounded-full" style={{ backgroundColor: '#1A4D2E' }}>
              <Upload className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: '#1A4D2E' }}>Subir Contrato</h3>
              <p className="text-xs text-gray-500">Analiza un documento</p>
            </div>
          </Link>

          <Link 
            to="/compare"
            className="bg-white border border-border shadow-sm hover:shadow-md transition-shadow rounded-lg p-5 flex items-center gap-4"
            data-testid="compare-card"
          >
            <div className="p-3 rounded-full bg-blue-100">
              <BarChart className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold">Comparar</h3>
              <p className="text-xs text-gray-500">Hasta 3 contratos</p>
            </div>
          </Link>

          <Link 
            to="/payments"
            className="bg-white border border-border shadow-sm hover:shadow-md transition-shadow rounded-lg p-5 flex items-center gap-4"
            data-testid="payments-card"
          >
            <div className="p-3 rounded-full bg-amber-100">
              <DollarSign className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold">Pagos</h3>
              <p className="text-xs text-gray-500">Historial</p>
            </div>
          </Link>

          {user.role === 'admin' && (
            <Link 
              to="/admin"
              className="bg-white border border-border shadow-sm hover:shadow-md transition-shadow rounded-lg p-5 flex items-center gap-4"
              data-testid="admin-card"
            >
              <div className="p-3 rounded-full bg-purple-100">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold">Admin</h3>
                <p className="text-xs text-gray-500">Panel</p>
              </div>
            </Link>
          )}

          <Link 
            to="/organization"
            className="bg-white border border-border shadow-sm hover:shadow-md transition-shadow rounded-lg p-5 flex items-center gap-4"
            data-testid="org-card"
          >
            <div className="p-3 rounded-full" style={{ backgroundColor: '#1A4D2E15' }}>
              <Building2 className="w-5 h-5" style={{ color: '#1A4D2E' }} />
            </div>
            <div>
              <h3 className="font-semibold">Equipo B2B</h3>
              <p className="text-xs text-gray-500">Organización</p>
            </div>
          </Link>
        </div>

        {/* Contracts List con Scroll Horizontal */}
        <div className="bg-white border border-border shadow-sm rounded-lg overflow-hidden">
          <div className="p-5 border-b border-border flex items-center justify-between">
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Merriweather, serif' }}>
              Mis Contratos
            </h2>
            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
              {contracts.length} total
            </span>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderColor: '#1A4D2E' }}></div>
            </div>
          ) : contracts.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 mb-4">Aún no has subido ningún contrato</p>
              <Link to="/upload">
                <Button style={{ backgroundColor: '#1A4D2E' }} data-testid="upload-first-btn">
                  Subir Mi Primer Contrato
                </Button>
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto w-full">
              <div className="min-w-[600px]">
                <div className="divide-y divide-border">
                  {contracts.map((contract) => (
                    <div 
                      key={contract.id}
                      className="p-4 hover:bg-gray-50 transition-colors"
                      data-testid={`contract-item-${contract.id}`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="p-2 rounded-lg bg-gray-100 flex-shrink-0">
                            <FileText className="w-4 h-4 text-gray-600" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-medium text-sm truncate">{contract.file_name}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500">
                                {new Date(contract.upload_date).toLocaleDateString('es-MX')}
                              </span>
                              {contract.contract_type && (
                                <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                                  {contract.contract_type.replace('_', ' ')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {contract.status === 'analyzed' && (
                            <RiskBadge level={getRiskFromScore(contract)} score={contract.risk_score} />
                          )}
                          
                          <span 
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              contract.status === 'analyzed' ? 'bg-green-100 text-green-700' :
                              contract.status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {contract.status === 'analyzed' ? 'Analizado' :
                             contract.status === 'processing' ? 'Procesando' : 'Subido'}
                          </span>
                          
                          <button
                            onClick={() => setEditingContract(contract.id)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Editar contrato"
                            data-testid={`edit-btn-${contract.id}`}
                          >
                            <Edit className="w-4 h-4 text-gray-500" />
                          </button>
                          
                          {contract.status === 'uploaded' && (
                            <Link to={`/payment?contract_id=${contract.id}&type=analysis`}>
                              <Button 
                                size="sm"
                                style={{ backgroundColor: '#1A4D2E' }}
                                data-testid={`analyze-btn-${contract.id}`}
                              >
                                Analizar
                              </Button>
                            </Link>
                          )}
                          {contract.status === 'analyzed' && (
                            <Link to={`/analysis/${contract.id}`}>
                              <Button size="sm" variant="outline" data-testid={`view-btn-${contract.id}`}>
                                Ver <ChevronRight className="w-4 h-4 ml-1" />
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white border border-border shadow-sm rounded-lg overflow-hidden mt-6">
          <div className="p-5 border-b border-border flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              Comparaciones
            </h2>
            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
              {comparisons.length} total
            </span>
          </div>

          {comparisons.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No hay comparaciones aún
            </div>
          ) : (
            <div className="divide-y divide-border">
              {comparisons.map((comp) => (
                <div key={comp.id} className="p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium">
                        Comparación #{comp.id}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(comp.created_at).toLocaleDateString()}
                      </p>
                    </div>

                    <Link to={`/comparisons/${comp.id}`}>
                      <Button size="sm" variant="outline">
                        Ver
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

      {/* Modals y Overlays */}
      {showAlerts && (
        <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setShowAlerts(false)}>
          <div className="fixed top-16 right-4 md:right-8 z-50" onClick={e => e.stopPropagation()}>
            <AlertsPanel onClose={() => setShowAlerts(false)} />
          </div>
        </div>
      )}

      {editingContract && (
        <ContractEditModal
          contractId={editingContract}
          onClose={() => setEditingContract(null)}
          onUpdate={fetchData}
          onDelete={fetchData}
        />
      )}

      {showOnboarding && (
        <OnboardingWizard onComplete={() => setShowOnboarding(false)} userId={user?.id} />
      )}
    </div>
  );
};

export default Dashboard;
